import { useState } from 'react';

import { Alert } from '@components/alert';
import { Button } from '@components/button';
import { Modal, hideModal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { LabeledSwitch } from '@components/switch/switch';
import { Text } from '@components/text';
import { TextArea } from '@components/textarea/textarea';
import { useScopedTranslate } from '@locale/translate';
import { pxToRem } from '@utils/converters';

export type AttributeModalProps = {
  attribute: {
    description: string;
    required: boolean;
    show: boolean;
  };
  onSave?: (updated: { description: string; required: boolean; show: boolean }) => void;
  readOnly?: boolean;
};

export const useAttributeModal = () => useModal(AttributeModal);

const AttributeModal = ({ attribute, onSave, readOnly }: AttributeModalProps) => {
  const { t } = useScopedTranslate(locale);
  const [description, setDescription] = useState(attribute.description || '');
  const [required, setRequired] = useState(attribute.required ?? false);
  const [show, setShow] = useState(attribute.show ?? true);

  const handleSave = () => {
    onSave?.({ description, required, show });
    hideModal();
  };

  const handleShowChange = (newShow: boolean) => {
    setShow(newShow);
    if (!newShow) {
      setRequired(false);
    }
  };

  return (
    <Modal>
      <Modal.SimpleHeader title={readOnly ? t('viewField') : t('editField')} />
      <Modal.Content>
        <Stack space={4}>
          {readOnly && <Alert variant="info">{t('readOnlyMessage')}</Alert>}
          <TextArea
            label={t('description')}
            placeholder={t('descriptionPlaceholder')}
            value={description}
            onChange={setDescription}
            style={{ minHeight: pxToRem(80) }}
            isDisabled={readOnly}
          />
          <Text font="regular14" color="textSecondary" text={t('descriptionHelp')} />
          <LabeledSwitch
            label={t('required')}
            helpText={t('requiredHelp')}
            helpTextPosition="bottom"
            value={required}
            onChange={setRequired}
            isDisabled={readOnly || !show}
          />
          <LabeledSwitch
            label={t('showHide')}
            helpText={t('showHideHelp')}
            helpTextPosition="bottom"
            value={show}
            onChange={handleShowChange}
            isDisabled={readOnly}
          />
        </Stack>
      </Modal.Content>
      <Modal.Footer>
        <Stack orientation="horizontal" space={4} justify="flex-end">
          {readOnly ? (
            <Button variant="secondary" onClick={hideModal} text={t('core.close')} />
          ) : (
            <>
              <Button variant="secondary" onClick={hideModal} text={t('core.cancel')} />
              <Button variant="primary-blue" onClick={handleSave} text={t('core.save')} />
            </>
          )}
        </Stack>
      </Modal.Footer>
    </Modal>
  );
};

const locale = {
  en: {
    editField: 'Edit Field',
    viewField: 'View Field',
    description: 'Description',
    descriptionPlaceholder: "E.g., 'Date when the employee will start working at the company.'",
    descriptionHelp:
      "Describe this field clearly so the AI accurately identifies and matches user-provided data (e.g., 'Date when the new employee starts work')",
    required: 'Required',
    requiredHelp: 'Is this field required to start the journey?',
    showHide: 'Show / Hide',
    showHideHelp:
      'Toggle this field off if you do not want the AI assistant to ask for data for this field',
    readOnlyMessage: 'This field cannot be modified as it is required for all journeys.',
  },
};
